import { computed, ref } from 'vue';
import { useCommunityStore } from '@/stores/community';
import { useCommunityCommentsStore } from '@/stores/community-comments';
import { useCommunityDmsStore } from '@/stores/community-dms';
import { useCommunityInteractionStore } from '@/stores/community-interactions';
import {
  COMMUNITY_INTERACTION_TYPES,
  COMMENT_DELETED_TOAST,
  COMMUNITY_INTERACTION_PLATFORM_BY_TYPE,
  conversationMessageTypes,
} from '@/app/community/constants';
import { useToast } from '@dashhudson/dashing-ui';
import communityApi from '@/apis/community';
import dayjs from 'dayjs';
import { refreshAbortController } from '@/apis/axios.utils';
import axios from 'axios';

export function useInteractionDetail(activeInteraction) {
  const communityStore = useCommunityStore();
  const commentStore = useCommunityCommentsStore();
  const dmStore = useCommunityDmsStore();
  const interactionStore = useCommunityInteractionStore();

  const { addToast } = useToast({ life: 5000 });

  const loadingDetail = ref(false);
  const interactionDetail = ref();
  const abortController = ref(null);

  const interaction = computed(
    () => activeInteraction?.value ?? interactionStore.selectedCommunityInteraction,
  );

  const conversationType = computed(
    () => interaction.value?.type && conversationMessageTypes().includes(interaction.value.type),
  );
  const commentType = computed(
    () => interaction.value?.type && !conversationMessageTypes().includes(interaction.value.type),
  );

  const interactionPlatform = computed(() => {
    if (interaction.value?.type) {
      return COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[interaction.value.type].platform;
    }
    return undefined;
  });

  const shouldFetchNestedTweet = computed(
    () =>
      interaction.value?.type === COMMUNITY_INTERACTION_TYPES.TWITTER_RETWEET &&
      !interaction.value?.mediaId &&
      commentStore.commentDetail?.retweetedStatus?.id,
  );

  const isTopLevelComment = computed(() => commentType.value && !interaction.value?.parentId);

  const shouldReturnParent = computed(() => {
    const noParentPlatforms = [
      COMMUNITY_INTERACTION_PLATFORM_BY_TYPE.TIKTOK_COMMENT.platform,
      COMMUNITY_INTERACTION_PLATFORM_BY_TYPE.YOUTUBE_COMMENT.platform,
      COMMUNITY_INTERACTION_PLATFORM_BY_TYPE.TWITTER_MENTION.platform,
    ];
    return !noParentPlatforms.includes(interactionPlatform.value) && !isTopLevelComment.value;
  });

  async function getInteractionMedia(axiosConfig = {}) {
    interactionStore.clearCommunityInteractionMedia();
    const { mediaId, brandId } = interaction.value;
    if (brandId && mediaId && commentType.value) {
      await interactionStore.setCommunityInteractionMedia({ brandId, mediaId }, axiosConfig);
    }
  }

  async function getCommentDetails({ refresh, axiosConfig = {} } = {}) {
    await getInteractionMedia(axiosConfig);
    const interactionId = interaction.value.id;
    const platform = interactionPlatform.value.toLowerCase();
    const params = { id: interactionId, platform };
    if (shouldReturnParent.value) {
      params.returnParent = !isTopLevelComment.value;
    }

    await commentStore.getCommentDetail({ ...params, refresh }, axiosConfig);
    if (shouldFetchNestedTweet.value) {
      await commentStore.getCommentDetail(
        {
          id: commentStore.commentDetail.retweetedStatus.id,
          platform,
          refresh: false,
          subsequentCall: true,
        },
        axiosConfig,
      );
    }
    interactionDetail.value = commentStore.commentDetail;
  }

  async function getConversationDetails({ refresh, internalNote, axiosConfig = {} } = {}) {
    const interactionId = interaction.value.id;
    const platform = interactionPlatform.value.toLowerCase();
    const params = { id: interactionId, platform };

    if (refresh) {
      dmStore.clearPlatformMessages(interactionId);
    }
    const createdAt = internalNote
      ? dayjs(internalNote.createdAt).add(7, 'day').format('YYYY-MM-DDTHH:mm:ss')
      : undefined;
    if (!dmStore.messages[interactionId]) {
      await dmStore.getPlatformMessages(
        {
          ...params,
          ...(createdAt ? { createdOnOrBefore: createdAt } : {}),
        },
        axiosConfig,
      );
    }
    const details = { internalNote: internalNote?.resourceId === interactionId };
    if (internalNote && refresh) {
      const res = await communityApi.getPlatformMessages(
        {
          ...params,
          createdOnOrAfter: createdAt,
        },
        axiosConfig,
      );
      details.hasNewerMessages = res.data.data.length > 0;
    }

    interactionDetail.value = {
      ...interaction.value,
      ...details,
    };
  }

  async function getInternalMessages() {
    // Get internal notes and system messages (automation and assignment records)
    try {
      await communityStore.getInternalMessages({
        resourceIds: [interaction.value.id],
        resourceTypes: communityStore.enabledResourceTypes,
        brandIds: [interaction.value.brandId],
      });
    } catch {
      if (interaction.value?.id) {
        addToast({
          message: 'Failed to load internal messages. Please try again later.',
          severity: 'error',
        });
      }
    }
  }

  function addRuleExecutionRecords(to, records) {
    const target = to ?? interaction.value;
    // `to` doesn't have a `type` property, so grab it from interaction if ids match
    const targetType =
      (target?.type ?? interaction.value.id === to.id) ? interaction.value.type : undefined;

    if (targetType && conversationMessageTypes().includes(targetType)) {
      dmStore.addRuleExecutionRecord(records, target);
    } else {
      commentStore.addRuleExecutionRecord(records, target);
    }
  }

  async function getInteractionDetail({ refresh = true, internalNote } = {}) {
    loadingDetail.value = true;
    if (!interaction.value?.id) {
      loadingDetail.value = false;
      return;
    }
    if (refresh) {
      interactionDetail.value = undefined;
      communityStore.clearInternalMessages();
    }
    const signal = refreshAbortController(abortController);

    try {
      if (conversationType.value) {
        await getConversationDetails({ refresh, internalNote, axiosConfig: { signal } });
      }
      if (commentType.value) {
        await getCommentDetails({ refresh, axiosConfig: { signal } });
      }
    } catch (error) {
      if (!axios.isCancel(error) && !!interaction.value?.id) {
        if (error?.response?.status === 404) {
          addToast({ message: COMMENT_DELETED_TOAST, severity: 'error' });
        } else {
          addToast({
            message: 'Message failed to load. Please try again later.',
            severity: 'error',
          });
        }
        loadingDetail.value = false;
      }
      return;
    }
    await getInternalMessages();

    loadingDetail.value = false;
  }

  return {
    interactionDetail,
    loadingDetail,
    conversationType,
    commentType,
    getCommentDetails,
    getConversationDetails,
    getInternalMessages,
    addRuleExecutionRecords,
    getInteractionDetail,
    getInteractionMedia,
  };
}
